import type { JSX } from 'react'

import { UnstyledButton } from '@buffer-mono/popcorn'

import { AttachmentTypes } from '~publish/legacy/constants'
import { LinkifiedText } from '~publish/legacy/shared-components/LinkifiedText'
import { parseSubstackLinks } from '~publish/legacy/utils/channels/substack/helpers'

import { DotsIcon } from '../../../../assets/substack'
import { useExpandablePreviewText } from '../../../Common/hooks/useExpandablePreviewText'
import useLinkPreviewEffect from '../../../Common/hooks/useLinkPreviewEffect'
import { ImagesGrid } from '../../../Common/ImagesGrid'
import { LinkPreview } from '../../../Common/LinkPreview'
import { gifAsImage } from '../../../Common/utils'
import { VideoPlayer } from '../../../Common/VideoPlayer'
import type { Content, ProfilePreview } from '../../../types'
import { ImageCarousel } from '../ImageCarousel'

import { SubstackNoteActions } from './SubstackNoteActions'
import styles from './SubstackNote.module.css'

export const SubstackNote = ({
  content,
  profile,
}: {
  content: Content
  profile: ProfilePreview
}): JSX.Element => {
  const {
    text,
    images,
    gif,
    video,
    link: linkFromPost,
    enabledAttachmentType,
  } = content
  const { avatar, username, displayName } = profile

  // Buffer sends Substack the URL only, so a card is shown for the attached
  // link alone and never scraped out of the text.
  const [linkPreview] = useLinkPreviewEffect({
    linkFromPost,
    text,
    useScraperData: false,
  })
  const { textToDisplay, isTruncated, isExpanded, expand } =
    useExpandablePreviewText({ channel: 'substack', text })

  const hasMedia = enabledAttachmentType === AttachmentTypes.MEDIA
  const allImages = hasMedia
    ? images?.length
      ? images
      : gif
        ? [gifAsImage(gif)]
        : []
    : []
  const hasImages = allImages.length > 0
  // A Note carries one video and nothing beside it, so the video wins the
  // media slot outright.
  const hasVideo = hasMedia && !!video
  const linkCard =
    enabledAttachmentType === AttachmentTypes.LINK && !hasImages && !hasVideo
      ? linkPreview
      : null

  return (
    <div className={styles.note}>
      <div className={styles.header}>
        <img
          className={styles.avatar}
          data-testid="avatar"
          src={avatar}
          alt="user avatar"
        />
        <span className={styles.displayName} translate="no">
          {displayName || username}
        </span>
        {/* Substack stamps a Note with its age. A Note this preview describes
            has not been published, so it can only ever read as brand new. */}
        <span className={styles.timestamp}>just now</span>
        <DotsIcon
          className={styles.menu}
          data-testid="substack-menu"
          size={18}
        />
      </div>

      {textToDisplay && (
        <div className={styles.text}>
          <LinkifiedText
            links={parseSubstackLinks(textToDisplay)}
            whitespace="pre-wrap"
            newTab
          >
            {textToDisplay}
          </LinkifiedText>
          {isTruncated && !isExpanded && (
            <UnstyledButton onClick={expand} className={styles.showMore}>
              See more
            </UnstyledButton>
          )}
        </div>
      )}

      {hasVideo && video && <VideoPlayer video={video} />}

      {!hasVideo && allImages.length > 1 && (
        <ImageCarousel className={styles.images} images={allImages} />
      )}

      {!hasVideo && allImages.length === 1 && (
        <ImagesGrid
          className={styles.images}
          images={allImages}
          maxHeight="240px"
          roundedCorners
          shouldShowGifTag
        />
      )}

      {linkCard && (
        <LinkPreview
          className={styles.linkCard}
          link={linkCard}
          clickable={false}
          imageCover
          showDescription
        />
      )}

      <SubstackNoteActions />
    </div>
  )
}
